import React, { useEffect, useState, useRef } from 'react';
import { Layers, DollarSign, AlertTriangle, TrendingUp, Clock, Activity, Zap, ChevronRight, ChevronDown, ChevronUp, Timer, UserCheck, ArrowUp, ArrowDown, Play, Pause, Eye, Trash2, GripVertical, Crown, Calendar, CreditCard, Info, CheckCircle2, ArrowRight, LayoutList, Target, ExternalLink, Database, Server, ShieldCheck, History, Receipt, AlertCircle, RefreshCw, Lock, Unlock, Smartphone, Mail, Settings, X, Monitor } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface AdQueueManagerProps {
  placements: any[];
  slotsData: any[];
  setDetailPanel: (panel: any) => void;
  setShowDeleteConfirm: (id: string) => void;
  moveQueueItem: (id: string, direction: number) => void;
  activateQueueItem: (id: string) => void;
  togglePlacementStatus: (id: string, status: string) => void;
  toggleFastTrack: (id: string) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// --- MOCK AUDIT LOGS ---
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: PLC-101 завершен (лимит показов). Активирован PLC-102.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для PLC-105. Статус изменен на "В очереди".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:35:18',
  type: 'conflict',
  message: 'Обнаружен конфликт дат для PLC-108 (Слот занят VIP-размещением).',
  status: 'warning'
}, {
  id: 'LOG-4',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 14 SMS-уведомлений о скором старте.',
  status: 'info'
}, {
  id: 'LOG-5',
  time: '10:15:22',
  type: 'lock',
  message: 'Slot Engine: Блокировка слота SLOT-04 снята (race condition prevented).',
  status: 'info'
}];
export function AdQueueManager({
  placements,
  slotsData,
  setDetailPanel,
  setShowDeleteConfirm,
  moveQueueItem,
  activateQueueItem,
  togglePlacementStatus,
  toggleFastTrack
}: AdQueueManagerProps) {
  // --- STATE ---
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [engineStatus, setEngineStatus] = useState<'active' | 'degraded' | 'error' | 'maintenance'>('active');
  const [logs, setLogs] = useState(initialLogs);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'conflict' | 'timing' | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [slotLocks, setSlotLocks] = useState<Record<string, boolean>>({});
  const [simulatedTime, setSimulatedTime] = useState(new Date());
  const [selectedPipelineSlot, setSelectedPipelineSlot] = useState<string>('all');
  // Local queue state for simulation
  const [localPlacements, setLocalPlacements] = useState(placements);
  // Sync with props
  useEffect(() => {
    setLocalPlacements(placements);
  }, [placements]);
  // --- SIMULATION ENGINE ---
  useEffect(() => {
    const timer = setInterval(() => {
      setSimulatedTime(new Date());
      // Simulate high-load engine ticks
      if (Math.random() > 0.95) {
        addLog('system', 'Redis cache sync completed (12ms)', 'info');
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  // --- HANDLERS ---
  const toggleExpand = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) newExpanded.delete(id);else newExpanded.add(id);
    setExpandedItems(newExpanded);
  };
  const handleAction = (action: string, item: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view':
        setDetailPanel({
          type: 'queue-item',
          title: 'Очередь: ' + item.object,
          data: item
        });
        break;
      case 'payment':
        setSelectedItem(item);
        setActiveModal('payment');
        break;
      case 'conflict':
        setSelectedItem(item);
        setActiveModal('conflict');
        break;
      case 'timing':
        setSelectedItem(item);
        setActiveModal('timing');
        break;
      case 'activate':
        if (item.payment !== 'paid' && item.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${item.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        // Simulate lock
        setSlotLocks(prev => ({
          ...prev,
          [item.slotName]: true
        }));
        addLog('lock', `Слот ${item.slotName} заблокирован для транзакции`, 'info');
        setTimeout(() => {
          activateQueueItem(item.id);
          setSlotLocks(prev => ({
            ...prev,
            [item.slotName]: false
          }));
          addLog('activation', `Размещение ${item.id} успешно активировано`, 'success');
        }, 800);
        break;
      case 'pause':
        togglePlacementStatus(item.id, 'paused');
        addLog('pause', `Размещение ${item.id} поставлено на паузу вручную`, 'warning');
        break;
      case 'fastTrack':
        toggleFastTrack(item.id);
        addLog('fast_track', `Fast Track ${item.fastTrack ? 'отключен' : 'включен'} для ${item.id}`, 'info');
        break;
      case 'delete':
        setShowDeleteConfirm(item.id);
        break;
    }
  };
  // --- DERIVED DATA ---
  const queuedItems = localPlacements.filter(p => p.status === 'queued' || p.status === 'paused').sort((a, b) => {
    // Fast track first, then priority, then position
    if (a.fastTrack && !b.fastTrack) return -1;
    if (!a.fastTrack && b.fastTrack) return 1;
    if (b.priority !== a.priority) return b.priority - a.priority;
    return (a.queuePosition || 0) - (b.queuePosition || 0);
  });
  const activeItems = localPlacements.filter(p => p.status === 'active');
  // --- RENDERERS ---
  const renderEngineStatus = () => <Card padding="sm" className="bg-slate-900 border-slate-800 text-white mb-6 shadow-xl" data-id="element-7692">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-2" data-id="element-7693">
        <div className="flex items-center gap-6" data-id="element-7694">
          <div className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setActiveModal('logs')} data-id="element-7695">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${engineStatus === 'active' ? 'bg-emerald-500/20 text-emerald-400' : engineStatus === 'degraded' ? 'bg-amber-500/20 text-amber-400' : 'bg-rose-500/20 text-rose-400'}`} data-id="element-7696">
              <Server className="w-5 h-5" data-id="element-7697" />
            </div>
            <div data-id="element-7698">
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-0.5" data-id="element-7699">
                Engine Status
              </div>
              <div className="flex items-center gap-2" data-id="element-7700">
                <span className="font-bold text-lg" data-id="element-7701">
                  {engineStatus === 'active' ? 'ACTIVE & HEALTHY' : engineStatus.toUpperCase()}
                </span>
                <div className={`w-2 h-2 rounded-full animate-pulse ${engineStatus === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} data-id="element-7702" />
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6 pl-6 border-l border-slate-700" data-id="element-7703">
            <div className="flex flex-col cursor-help" title="Redis Cache Status: Connected" data-id="element-7704">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-7705">
                Cache (Redis)
              </span>
              <span className="text-sm font-medium text-emerald-400 flex items-center gap-1" data-id="element-7706">
                <Database className="w-3.5 h-3.5" data-id="element-7707" /> HIT 99.8%
              </span>
            </div>
            <div className="flex flex-col cursor-help" title="Async Queue Workers: 12 Active" data-id="element-7708">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-7709">
                Workers
              </span>
              <span className="text-sm font-medium text-blue-400 flex items-center gap-1" data-id="element-7710">
                <Activity className="w-3.5 h-3.5" data-id="element-7711" /> 12 Active
              </span>
            </div>
            <div className="flex flex-col cursor-help" title="Average Response Time" data-id="element-7712">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-7713">
                Response
              </span>
              <span className="text-sm font-medium text-emerald-400 flex items-center gap-1" data-id="element-7714">
                <Zap className="w-3.5 h-3.5" data-id="element-7715" /> 42ms
              </span>
            </div>
            <div className="flex flex-col cursor-help" title="Notification Service" data-id="element-7716">
              <span className="text-[10px] text-slate-400 uppercase font-bold" data-id="element-7717">
                SMS / Email
              </span>
              <span className="text-sm font-medium text-indigo-400 flex items-center gap-1" data-id="element-7718">
                <Mail className="w-3.5 h-3.5" data-id="element-7719" /> 142 sent today
              </span>
            </div>
          </div>
        </div>

        <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white shrink-0" icon={<History className="w-4 h-4" data-id="element-7721" />} onClick={() => setActiveModal('logs')} data-id="element-7720">
          Audit Logs
        </Button>
      </div>
    </Card>;
  const renderAutoLogicPipeline = () => {
    // Get unique slot names from placements + slotsData
    const allSlotNames = Array.from(new Set([...slotsData.map(s => s.name), ...localPlacements.map(p => p.slotName).filter(Boolean)]));
    // Filter by selected slot
    const filteredActive = selectedPipelineSlot === 'all' ? activeItems : activeItems.filter(p => p.slotName === selectedPipelineSlot);
    const filteredQueued = selectedPipelineSlot === 'all' ? queuedItems : queuedItems.filter(p => p.slotName === selectedPipelineSlot);
    const selectedSlotData = slotsData.find(s => s.name === selectedPipelineSlot);
    const isLocked = selectedPipelineSlot === 'all' ? Object.values(slotLocks).some(v => v) : slotLocks[selectedPipelineSlot];
    // For "all" mode, show summary; for specific slot, show pipeline
    const active = filteredActive[0] || null;
    const next1 = filteredQueued[0];
    const next2 = filteredQueued[1];
    const next3 = filteredQueued[2];
    return <Card padding="none" className="mb-6 rounded-xl shadow-sm border-slate-200 overflow-hidden bg-slate-50/50" data-id="element-7722">
        {/* Header with slot selector */}
        <div className="p-4 border-b border-slate-200 bg-white" data-id="element-7723">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3" data-id="element-7724">
            <div className="flex items-center gap-3" data-id="element-7725">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-7726">
                <RefreshCw className="w-4 h-4 text-indigo-600" data-id="element-7727" />
              </div>
              <div data-id="element-7728">
                <h3 className="text-sm font-bold text-slate-900" data-id="element-7729">
                  Автоматическая ротация (Pipeline)
                </h3>
                <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-7730">
                  {selectedPipelineSlot === 'all' ? `Обзор всех ${allSlotNames.length} слотов` : `Движок для слота: ${selectedPipelineSlot}`}
                </p>
              </div>
            </div>
            <Badge variant={isLocked ? 'warning' : 'success'} className="animate-pulse flex items-center gap-1" data-id="element-7731">
              {isLocked ? <Lock className="w-3 h-3" data-id="element-7732" /> : <Unlock className="w-3 h-3" data-id="element-7733" />}
              {isLocked ? 'SLOT LOCKED' : 'SLOT READY'}
            </Badge>
          </div>

          {/* Slot selector tabs */}
          <div className="flex flex-wrap gap-1.5" data-id="element-7734">
            <button className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedPipelineSlot === 'all' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} onClick={() => setSelectedPipelineSlot('all')} data-id="element-7735">
              <span className="flex items-center gap-1.5" data-id="element-7736">
                <Monitor className="w-3 h-3" data-id="element-7737" />
                Все слоты
              </span>
            </button>
            {allSlotNames.map(slotName => {
            const slotInfo = slotsData.find(s => s.name === slotName);
            const slotActive = activeItems.filter(p => p.slotName === slotName).length;
            const slotQueued = queuedItems.filter(p => p.slotName === slotName).length;
            const hasConflict = queuedItems.some(p => p.slotName === slotName && p.conflict);
            return <button key={slotName} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${selectedPipelineSlot === slotName ? 'bg-indigo-600 text-white shadow-sm' : hasConflict ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} onClick={() => setSelectedPipelineSlot(slotName)} data-id="element-7738">
                  {slotName}
                  {slotActive > 0 && <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${selectedPipelineSlot === slotName ? 'bg-white/20' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-7739">
                      {slotActive}
                    </span>}
                  {slotQueued > 0 && <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${selectedPipelineSlot === slotName ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`} data-id="element-7740">
                      +{slotQueued}
                    </span>}
                </button>;
          })}
          </div>
        </div>

        {/* Pipeline content */}
        {selectedPipelineSlot === 'all' /* ALL SLOTS OVERVIEW */ ? <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-7741">
            {allSlotNames.map(slotName => {
          const slotInfo = slotsData.find(s => s.name === slotName);
          const slotActiveItems = activeItems.filter(p => p.slotName === slotName);
          const slotQueuedItems = queuedItems.filter(p => p.slotName === slotName);
          const currentActive = slotActiveItems[0];
          const nextUp = slotQueuedItems[0];
          const locked = slotLocks[slotName];
          return <div key={slotName} className="bg-white p-4 rounded-xl border border-slate-200 hover:border-indigo-300 cursor-pointer transition-all hover:shadow-sm" onClick={() => setSelectedPipelineSlot(slotName)} data-id="element-7742">
                  <div className="flex justify-between items-start mb-3" data-id="element-7743">
                    <div data-id="element-7744">
                      <div className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-7745">
                        {slotName}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-7746">
                        {slotInfo?.page} • {slotInfo?.type} • {slotInfo?.size}
                      </div>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-7747">
                      {locked && <Lock className="w-3 h-3 text-amber-500" data-id="element-7748" />}
                      <div className={`w-2 h-2 rounded-full ${currentActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} data-id="element-7749" />
                    </div>
                  </div>

                  {currentActive ? <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 mb-2" data-id="element-7750">
                      <div className="flex items-center gap-1.5 text-[10px] text-emerald-700 font-bold mb-1" data-id="element-7751">
                        <Play className="w-3 h-3 fill-current" data-id="element-7752" /> Транслируется
                      </div>
                      <div className="text-xs font-bold text-slate-900 truncate" data-id="element-7753">
                        {currentActive.object}
                      </div>
                      <div className="flex items-center gap-2 mt-1" data-id="element-7754">
                        <div className="flex-1 bg-emerald-100 rounded-full h-1" data-id="element-7755">
                          <div className="bg-emerald-500 h-1 rounded-full" style={{
                    width: `${Math.min(100, currentActive.impressionsFact / currentActive.impressionsPlan * 100)}%`
                  }} data-id="element-7756" />
                        </div>
                        <span className="text-[9px] text-emerald-600 font-medium" data-id="element-7757">
                          {Math.round(currentActive.impressionsFact / currentActive.impressionsPlan * 100)}
                          %
                        </span>
                      </div>
                    </div> : <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-2.5 mb-2 text-center" data-id="element-7758">
                      <span className="text-[10px] text-slate-400 font-medium" data-id="element-7759">
                        Нет активных
                      </span>
                    </div>}

                  {nextUp ? <div className="flex items-center gap-2 text-[10px] text-slate-600" data-id="element-7760">
                      <ArrowRight className="w-3 h-3 text-indigo-400" data-id="element-7761" />
                      <span data-id="element-7762">
                        Следующий:{' '}
                        <strong className="text-slate-900" data-id="element-7763">
                          {nextUp.object}
                        </strong>
                      </span>
                      {nextUp.fastTrack && <Zap className="w-3 h-3 text-amber-500" data-id="element-7764" />}
                    </div> : <div className="flex items-center gap-2 text-[10px] text-slate-400" data-id="element-7765">
                      <ArrowRight className="w-3 h-3" data-id="element-7766" />
                      <span data-id="element-7767">
                        Очередь пуста • Fallback: {slotInfo?.fallback || 'hide'}
                      </span>
                    </div>}

                  {slotQueuedItems.length > 1 && <div className="mt-1.5 text-[9px] text-slate-400" data-id="element-7768">
                      + ещё {slotQueuedItems.length - 1} в очереди
                    </div>}
                </div>;
        })}
          </div> /* SINGLE SLOT PIPELINE */ : <div className="p-6 overflow-x-auto hide-scrollbar" data-id="element-7769">
            {/* Slot info bar */}
            {selectedSlotData && <div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-slate-100" data-id="element-7770">
                <div className="flex items-center gap-2 text-[10px] text-slate-500" data-id="element-7771">
                  <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-7772" />
                  <span className="font-bold text-slate-700" data-id="element-7773">
                    {selectedSlotData.page}
                  </span>
                  <span data-id="element-7774">•</span>
                  <span data-id="element-7775">{selectedSlotData.type}</span>
                  <span data-id="element-7776">•</span>
                  <span data-id="element-7777">{selectedSlotData.size}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px]" data-id="element-7778">
                  <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-7779">
                    Лимит: {selectedSlotData.limit}
                  </Badge>
                  <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-7780">
                    Ротация: {selectedSlotData.rotation}
                  </Badge>
                  <Badge variant="neutral" size="sm" className="text-[9px]" data-id="element-7781">
                    Fallback: {selectedSlotData.fallback}
                  </Badge>
                </div>
              </div>}

            <div className="flex items-center min-w-[800px] gap-4" data-id="element-7782">
              {/* ACTIVE */}
              {active ? <div className="flex-1 bg-white p-4 rounded-xl border-2 border-emerald-400 shadow-[0_0_20px_rgba(52,211,153,0.15)] relative cursor-pointer hover:bg-emerald-50/30 transition-colors" onClick={() => handleAction('view', active)} data-id="element-7783">
                  <div className="absolute -top-3 left-4 bg-emerald-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-7784">
                    Транслируется
                  </div>
                  <div className="flex justify-between items-start mt-2 mb-2" data-id="element-7785">
                    <div className="font-bold text-slate-900" data-id="element-7786">
                      {active.object}
                    </div>
                    <div className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600" data-id="element-7787">
                      {active.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-id="element-7788">
                    <Timer className="w-3.5 h-3.5 text-emerald-500" data-id="element-7789" />
                    Осталось:{' '}
                    <span className="font-bold text-slate-900" data-id="element-7790">
                      {active.timer || active.daysLeft + ' дн.'}
                    </span>
                  </div>
                  <div className="space-y-1" data-id="element-7791">
                    <div className="flex justify-between text-[10px] text-slate-500" data-id="element-7792">
                      <span data-id="element-7793">Лимит показов</span>
                      <span data-id="element-7794">
                        {active.impressionsFact?.toLocaleString()} /{' '}
                        {active.impressionsPlan?.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-7795">
                      <div className="bg-emerald-400 h-1.5 rounded-full transition-all" style={{
                  width: `${Math.min(100, (active.impressionsFact || 0) / (active.impressionsPlan || 1) * 100)}%`
                }} data-id="element-7796" />
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500 flex items-center gap-1" data-id="element-7797">
                    <UserCheck className="w-3 h-3" data-id="element-7798" /> {active.advertiser}
                  </div>
                </div> : <div className="flex-1 bg-slate-50 p-4 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 min-h-[160px]" data-id="element-7799">
                  <Layers className="w-6 h-6 mb-2 opacity-50" data-id="element-7800" />
                  <span className="text-xs font-medium" data-id="element-7801">
                    Нет активных размещений
                  </span>
                  <span className="text-[10px] mt-1" data-id="element-7802">
                    Fallback: {selectedSlotData?.fallback || 'default banner'}
                  </span>
                </div>}

              {/* ARROW */}
              <div className="flex flex-col items-center justify-center px-2" data-id="element-7803">
                <div className="text-[9px] font-bold text-indigo-600 uppercase tracking-wider mb-1 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100" data-id="element-7804">
                  onExpire()
                </div>
                <div className="flex items-center w-16" data-id="element-7805">
                  <div className="h-0.5 bg-indigo-200 flex-1" data-id="element-7806" />
                  <ArrowRight className="w-4 h-4 text-indigo-400" data-id="element-7807" />
                </div>
              </div>

              {/* NEXT 1 */}
              {next1 ? <div className="flex-1 bg-white p-4 rounded-xl border-2 border-indigo-200 shadow-sm relative cursor-pointer hover:border-indigo-400 transition-colors" onClick={() => handleAction('view', next1)} data-id="element-7808">
                  <div className="absolute -top-3 left-4 bg-indigo-100 text-indigo-700 border border-indigo-200 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-7809">
                    Следующий (#1)
                  </div>
                  <div className="flex justify-between items-start mt-2 mb-2" data-id="element-7810">
                    <div className="font-bold text-slate-900" data-id="element-7811">
                      {next1.object}
                    </div>
                    <div className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600" data-id="element-7812">
                      {next1.id}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 mb-3" data-id="element-7813">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-600" data-id="element-7814">
                      <Calendar className="w-3 h-3 text-indigo-400" data-id="element-7815" />
                      Старт:{' '}
                      <span className="font-bold text-slate-900" data-id="element-7816">
                        {next1.slotFreeDate || 'Сразу после освобождения'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-600" data-id="element-7817">
                      <Info className="w-3 h-3 text-amber-500" data-id="element-7818" />
                      Причина:{' '}
                      <span className="font-bold text-amber-700 bg-amber-50 px-1 rounded" data-id="element-7819">
                        {next1.fastTrack ? 'Fast Track' : next1.priority >= 8 ? 'Highest Priority' : 'Earliest Booking'}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-100 flex justify-between items-center" data-id="element-7820">
                    <Badge variant={next1.payment === 'paid' ? 'success' : 'warning'} size="sm" className="text-[9px]" data-id="element-7821">
                      {next1.payment === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={e => handleAction('activate', next1, e)} data-id="element-7822">
                      Активировать
                    </Button>
                  </div>
                </div> : <div className="flex-1 bg-slate-50 p-4 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 min-h-[140px]" data-id="element-7823">
                  <Layers className="w-6 h-6 mb-2 opacity-50" data-id="element-7824" />
                  <span className="text-xs font-medium" data-id="element-7825">Очередь пуста</span>
                  <span className="text-[10px] mt-1" data-id="element-7826">
                    Fallback: {selectedSlotData?.fallback || 'default banner'}
                  </span>
                </div>}

              {/* ARROW */}
              <div className="flex items-center w-8" data-id="element-7827">
                <div className="h-0.5 bg-slate-200 flex-1" data-id="element-7828" />
                <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-7829" />
              </div>

              {/* NEXT 2 & 3 */}
              <div className="w-48 shrink-0 flex flex-col gap-2" data-id="element-7830">
                {next2 ? <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm opacity-80 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => handleAction('view', next2)} data-id="element-7831">
                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-7832">
                      В очереди (#2)
                    </div>
                    <div className="font-bold text-slate-800 text-sm truncate" data-id="element-7833">
                      {next2.object}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1" data-id="element-7834">
                      <Clock className="w-3 h-3" data-id="element-7835" /> Ожидание ~
                      {next2.daysLeft || '?'}д
                    </div>
                  </div> : <div className="bg-slate-50 p-3 rounded-xl border border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 min-h-[60px]" data-id="element-7836">
                    <span className="text-[10px] font-medium" data-id="element-7837">Пусто (#2)</span>
                  </div>}
                {next3 ? <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm opacity-60 cursor-pointer hover:opacity-100 transition-opacity" onClick={() => handleAction('view', next3)} data-id="element-7838">
                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-7839">
                      В очереди (#3)
                    </div>
                    <div className="font-bold text-slate-800 text-sm truncate" data-id="element-7840">
                      {next3.object}
                    </div>
                  </div> : <div className="bg-slate-50 p-2.5 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-400" data-id="element-7841">
                    <span className="text-[9px] font-medium" data-id="element-7842">Fallback</span>
                  </div>}
              </div>
            </div>
          </div>}
      </Card>;
  };
  const renderQueueTable = () => {
    return <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-7843">
        <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-7844">
          <h3 className="text-sm font-bold text-slate-900" data-id="element-7845">
            Детальная очередь
          </h3>
          <div className="flex gap-2" data-id="element-7846">
            <Badge variant="neutral" size="sm" data-id="element-7847">
              Всего: {queuedItems.length}
            </Badge>
            <Badge variant="error" size="sm" data-id="element-7848">
              Конфликтов: {queuedItems.filter(q => q.conflict).length}
            </Badge>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-4 px-4 py-3 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200" data-id="element-7849">
          <div className="w-12 shrink-0 text-center" data-id="element-7850">Поз.</div>
          <div className="flex-1 min-w-[200px]" data-id="element-7851">ID / Объект</div>
          <div className="w-[160px] shrink-0" data-id="element-7852">Слот / Приоритет</div>
          <div className="w-[140px] shrink-0" data-id="element-7853">Оплата / Статус</div>
          <div className="w-[180px] shrink-0" data-id="element-7854">Тайминг</div>
          <div className="w-[180px] shrink-0 text-right" data-id="element-7855">Действия</div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-7856">
          {queuedItems.map((q, index) => {
          const isConflict = q.conflict;
          const isPaid = q.payment === 'paid' || q.paymentStatus === 'paid';
          return <div key={q.id} className={`flex flex-col lg:flex-row lg:items-center gap-4 p-4 transition-colors ${isConflict ? 'bg-rose-50/30' : 'bg-white hover:bg-slate-50'}`} data-id="element-7857">
                {/* Position */}
                <div className="w-12 shrink-0 items-center justify-center gap-1 hidden lg:flex" data-id="element-7858">
                  <GripVertical className="w-3 h-3 text-slate-300 cursor-grab hover:text-slate-500" data-id="element-7859" />
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-slate-50 text-slate-500 border-slate-200'}`} data-id="element-7860">
                    #{index + 1}
                  </div>
                </div>

                {/* Identity */}
                <div className="flex-1 min-w-[200px]" data-id="element-7861">
                  <div className="flex items-center gap-2 mb-1" data-id="element-7862">
                    <div className="text-[10px] font-bold text-indigo-600 font-mono bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view', q)} data-id="element-7863">
                      {q.id}
                    </div>
                    {q.fastTrack && <Badge variant="warning" size="sm" className="text-[9px] py-0" data-id="element-7864">
                        <Zap className="w-2.5 h-2.5 mr-0.5" data-id="element-7865" /> Fast Track
                      </Badge>}
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-0.5 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view', q)} data-id="element-7866">
                    {q.object} <ExternalLink className="w-3 h-3 opacity-50" data-id="element-7867" />
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1" data-id="element-7868">
                    <UserCheck className="w-3 h-3" data-id="element-7869" /> {q.advertiser}
                  </div>
                </div>

                {/* Slot & Priority */}
                <div className="w-full lg:w-[160px] shrink-0" data-id="element-7870">
                  <div className="text-xs font-bold text-slate-900 mb-1.5 flex items-center gap-1.5" data-id="element-7871">
                    <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-7872" />
                    {q.slotName}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-7873">
                    <Badge variant={q.priority >= 8 ? 'error' : q.priority >= 5 ? 'warning' : 'neutral'} size="sm" className="text-[9px]" data-id="element-7874">
                      {q.priority >= 8 ? <Crown className="w-2.5 h-2.5 mr-1" data-id="element-7875" /> : null}
                      Приоритет: {q.priority}/10
                    </Badge>
                  </div>
                </div>

                {/* Payment & Status */}
                <div className="w-full lg:w-[140px] shrink-0 flex flex-col gap-1.5" data-id="element-7876">
                  <div className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', q)} title="Нажмите для просмотра деталей оплаты" data-id="element-7877">
                    <Receipt className="w-3 h-3" data-id="element-7878" />
                    {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                  </div>

                  <div className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded cursor-help" title={q.statusReason === 'slot_full' ? 'Слот занят другим размещением' : q.statusReason === 'payment_pending' ? 'Ожидается подтверждение оплаты' : q.statusReason === 'date_conflict' ? 'Конфликт дат с другим размещением' : q.statusReason === 'waiting_start' ? 'Ожидание наступления даты старта' : q.statusReason === 'manual_pause' ? 'Поставлено на паузу вручную' : q.statusReason} data-id="element-7879">
                    <Info className="w-3 h-3 text-slate-400" data-id="element-7880" />
                    {q.statusReason === 'slot_full' ? 'Слот занят' : q.statusReason}
                  </div>
                </div>

                {/* Timing & Conflicts */}
                <div className="w-full lg:w-[180px] shrink-0 flex flex-col gap-1.5" data-id="element-7881">
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer hover:text-indigo-600" onClick={() => handleAction('timing', q)} data-id="element-7882">
                    <Calendar className="w-3.5 h-3.5 text-indigo-400" data-id="element-7883" />
                    <span className="font-medium" data-id="element-7884">
                      Старт: {q.slotFreeDate || 'Сразу'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500" data-id="element-7885">
                    <Clock className="w-3.5 h-3.5" data-id="element-7886" />
                    Ожидание: ~{q.daysLeft || 2} дн.
                  </div>

                  {isConflict && <div className="flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100 cursor-pointer hover:bg-rose-100 mt-1" onClick={() => handleAction('conflict', q)} data-id="element-7887">
                      <AlertTriangle className="w-3 h-3" data-id="element-7888" /> Конфликт дат
                    </div>}
                </div>

                {/* Actions */}
                <div className="w-full lg:w-[180px] shrink-0 flex flex-wrap items-center justify-end gap-1 mt-2 lg:mt-0" data-id="element-7889">
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Поднять" onClick={() => moveQueueItem(q.id, -1)} data-id="element-7890">
                    <ArrowUp className="w-4 h-4" data-id="element-7891" />
                  </Button>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Опустить" onClick={() => moveQueueItem(q.id, 1)} data-id="element-7892">
                    <ArrowDown className="w-4 h-4" data-id="element-7893" />
                  </Button>
                  <Button variant="ghost" size="sm" className={`w-8 h-8 p-0 ${q.fastTrack ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500'}`} title="Fast Track" onClick={() => handleAction('fastTrack', q)} data-id="element-7894">
                    <Zap className="w-4 h-4" data-id="element-7895" />
                  </Button>
                  <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-7896" />
                  {q.status === 'paused' ? <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-emerald-500 hover:bg-emerald-50" title="Возобновить" onClick={() => togglePlacementStatus(q.id, 'queued')} data-id="element-7897">
                      <Play className="w-4 h-4" data-id="element-7898" />
                    </Button> : <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', q)} data-id="element-7899">
                      <Pause className="w-4 h-4" data-id="element-7900" />
                    </Button>}
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-indigo-600 hover:bg-indigo-50" title="Принудительный старт" onClick={e => handleAction('activate', q, e)} data-id="element-7901">
                    <Play className="w-4 h-4 fill-current" data-id="element-7902" />
                  </Button>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', q)} data-id="element-7903">
                    <Trash2 className="w-4 h-4" data-id="element-7904" />
                  </Button>
                </div>
              </div>;
        })}

          {queuedItems.length === 0 && <div className="p-12 text-center flex flex-col items-center justify-center text-slate-500" data-id="element-7905">
              <Layers className="w-12 h-12 mb-4 text-slate-300" data-id="element-7906" />
              <h4 className="text-lg font-bold text-slate-900 mb-1" data-id="element-7907">
                Очередь пуста
              </h4>
              <p className="text-sm" data-id="element-7908">
                Все рекламные размещения активны или завершены.
              </p>
            </div>}
        </div>
      </Card>;
  };
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-7909">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-7910">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-7911">
              <div className="flex items-center gap-3" data-id="element-7912">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-7913" />
                <div data-id="element-7914">
                  <h3 className="font-bold" data-id="element-7915">Системные логи Engine</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-7916">
                    Аудит автоматических действий и ошибок
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-7918" />} onClick={() => setActiveModal(null)} data-id="element-7917" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-7919">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-7920">
                  <div className="text-slate-500 shrink-0" data-id="element-7921">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-7922">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-7923">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedItem && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-7924">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-7925">
              <div className="flex items-center gap-2" data-id="element-7926">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-7927" />
                <h3 className="font-bold text-slate-900" data-id="element-7928">Детали оплаты</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-7930" />} onClick={() => setActiveModal(null)} data-id="element-7929" />
            </div>
            <div className="p-6 space-y-6" data-id="element-7931">
              <div className="text-center" data-id="element-7932">
                <div className="text-sm text-slate-500 mb-1" data-id="element-7933">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-7934">
                  {formatCurrency(selectedItem.price || 0)}
                </div>
                <Badge variant={selectedItem.payment === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-7935">
                  {selectedItem.payment === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-7936">
                <div className="flex justify-between" data-id="element-7937">
                  <span className="text-slate-500" data-id="element-7938">ID Размещения</span>
                  <span className="font-bold text-slate-900" data-id="element-7939">
                    {selectedItem.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-7940">
                  <span className="text-slate-500" data-id="element-7941">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-7942">
                    INV-{selectedItem.id.split('-')[1]}-2026
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-7943">
                  <span className="text-slate-500" data-id="element-7944">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-7945">
                    {selectedItem.advertiser}
                  </span>
                </div>
              </div>

              {selectedItem.payment !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-7946">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-7947" />
                  <div className="text-sm text-amber-800" data-id="element-7948">
                    <strong data-id="element-7949">Блокировка активации:</strong> Размещение не может
                    быть запущено до подтверждения оплаты. Система автоматически
                    пропустит его в очереди.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-7950">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-7951">
                  Закрыть
                </Button>
                {selectedItem.payment !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              addLog('payment', `Ручное подтверждение оплаты для ${selectedItem.id}`, 'success');
              setActiveModal(null);
              alert('Оплата подтверждена (симуляция)');
            }} data-id="element-7952">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* CONFLICT MODAL */}
        {activeModal === 'conflict' && selectedItem && <Card className="w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-7953">
            <div className="p-4 border-b border-rose-200 bg-rose-50 flex justify-between items-center" data-id="element-7954">
              <div className="flex items-center gap-2 text-rose-700" data-id="element-7955">
                <AlertCircle className="w-5 h-5" data-id="element-7956" />
                <h3 className="font-bold" data-id="element-7957">Разрешение конфликта</h3>
              </div>
              <Button variant="ghost" size="sm" className="text-rose-700 hover:bg-rose-100" icon={<X className="w-5 h-5" data-id="element-7959" />} onClick={() => setActiveModal(null)} data-id="element-7958" />
            </div>
            <div className="p-6 space-y-6" data-id="element-7960">
              <div className="text-sm text-slate-700" data-id="element-7961">
                Обнаружен конфликт для размещения{' '}
                <strong data-id="element-7962">
                  {selectedItem.id} ({selectedItem.object})
                </strong>
                .
              </div>

              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-800" data-id="element-7963">
                <strong data-id="element-7964">Причина:</strong>{' '}
                {selectedItem.conflictReason || 'Слот занят VIP-размещением на выбранные даты. Невозможно гарантировать лимит показов.'}
              </div>

              <div className="space-y-3" data-id="element-7965">
                <h4 className="font-bold text-slate-900 text-sm" data-id="element-7966">
                  Варианты решения:
                </h4>

                <button className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex gap-3 group" onClick={() => {
              addLog('conflict_resolve', `Конфликт ${selectedItem.id} решен: смена дат`, 'success');
              setActiveModal(null);
            }} data-id="element-7967">
                  <Calendar className="w-5 h-5 text-indigo-500 shrink-0" data-id="element-7968" />
                  <div data-id="element-7969">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-700" data-id="element-7970">
                      Сдвинуть даты старта
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-7971">
                      Автоматически перенести старт на первую свободную дату
                      (ожидание +4 дня)
                    </div>
                  </div>
                </button>

                <button className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex gap-3 group" onClick={() => {
              addLog('conflict_resolve', `Конфликт ${selectedItem.id} решен: смена слота`, 'success');
              setActiveModal(null);
            }} data-id="element-7972">
                  <LayoutList className="w-5 h-5 text-indigo-500 shrink-0" data-id="element-7973" />
                  <div data-id="element-7974">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-700" data-id="element-7975">
                      Предложить другой слот
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-7976">
                      Перенести в слот "Боковой баннер" со скидкой 15%
                    </div>
                  </div>
                </button>

                <button className="w-full text-left p-4 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors flex gap-3 group" onClick={() => {
              addLog('conflict_override', `Конфликт ${selectedItem.id} проигнорирован (Override)`, 'warning');
              setActiveModal(null);
            }} data-id="element-7977">
                  <ShieldCheck className="w-5 h-5 text-rose-600 shrink-0" data-id="element-7978" />
                  <div data-id="element-7979">
                    <div className="font-bold text-rose-900" data-id="element-7980">
                      Override (Принудительно)
                    </div>
                    <div className="text-xs text-rose-700 mt-1" data-id="element-7981">
                      Игнорировать конфликт. Возможна недокрутка показов у обоих
                      рекламодателей.
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedItem && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-7982">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-7983">
              <div className="flex items-center gap-2" data-id="element-7984">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-7985" />
                <h3 className="font-bold text-slate-900" data-id="element-7986">Детали тайминга</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-7988" />} onClick={() => setActiveModal(null)} data-id="element-7987" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-7989">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-7990">
                <span className="text-slate-500" data-id="element-7991">Тип старта</span>
                <Badge variant="neutral" data-id="element-7992">
                  {selectedItem.startType === 'immediate' ? 'По готовности' : 'Фикс. дата'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-7993">
                <span className="text-slate-500" data-id="element-7994">Ожидаемый старт</span>
                <span className="font-bold text-slate-900" data-id="element-7995">
                  {selectedItem.slotFreeDate || 'Неизвестно'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-7996">
                <span className="text-slate-500" data-id="element-7997">Время ожидания</span>
                <span className="font-medium text-indigo-600" data-id="element-7998">
                  {selectedItem.daysLeft ? `~${selectedItem.daysLeft} дней` : '—'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-7999">
                <span className="text-slate-500" data-id="element-8000">Дедлайн старта</span>
                <span className="font-medium text-slate-900" data-id="element-8001">
                  {selectedItem.deadline || 'Нет'}
                </span>
              </div>

              <div className="mt-6 bg-blue-50 p-4 rounded-xl text-xs text-blue-800 flex gap-2" data-id="element-8002">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-8003" />
                <p data-id="element-8004">
                  Время старта рассчитывается динамически на основе текущей
                  скорости открутки показов активного баннера.
                </p>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-8005">
      {renderEngineStatus()}
      {renderAutoLogicPipeline()}
      {renderQueueTable()}
      {renderModals()}
    </div>;
}